/* eslint-disable jsdoc/require-jsdoc */
import { Column, Exhibit } from './styled.jsx';
import { Label } from '@studio/design-system/label';
import type { T_Range, WiringOptions } from '@studio/design-system/slider';
import * as Slider from '@studio/design-system/slider';
import { toRange } from '@studio/js-utils/range';
import { useLazyRef } from '@studio/react-utils/hooks';
import { useState } from 'react';
import type { ReactElement } from 'react';

const PlusMinusOne: T_Range = toRange(-1, 1);

export const Gain = (): ReactElement => {
  const [value, setValue] = useState<readonly number[]>([50]);

  const wiring = Slider.useWiring(value, {
    onChange: setValue,
    step: 5
  });

  return (
    <Slider.Container label='Gain'>
      <Slider.Archetype {...wiring.sliderProps} />
      <Slider.Input {...wiring.inputProps} />
    </Slider.Container>
  );
};

export const Loudness = (): ReactElement => {
  const [value, setValue] = useState<readonly number[]>([100]);

  const wiring = Slider.useWiring(
    value,
    useLazyRef(() => ({
      input: {
        formatValue: (v: readonly number[]) => `${Slider.format(v)}%`,
        range: Slider.DefaultRange
      },
      onChange: setValue,
      range: { max: 10, min: 0 },
      step: 1
    })).current
  );

  return (
    <div>
      <Slider.Container label='Loudness'>
        <Slider.Root {...wiring.sliderProps}>
          <Slider.Track />
          <Slider.Thumb />
        </Slider.Root>
        <Slider.Input {...wiring.inputProps} />
      </Slider.Container>
      <Slider.RangeLabel>
        <Label>Left</Label>
        <Label>Right</Label>
      </Slider.RangeLabel>
    </div>
  );
};

export const Presence = (): ReactElement => {
  const [value, setValue] = useState<readonly number[]>([0]);

  const wiring = Slider.useWiring(
    value,
    useLazyRef(() => ({
      input: {
        range: { max: 50, min: -50 }
      },
      onChange: setValue,
      range: PlusMinusOne,
      slider: {
        range: Slider.DefaultRange
      },
      step: 0.1
    })).current
  );

  return (
    <Slider.Container label='Presence'>
      <Slider.Root {...wiring.sliderProps}>
        <Slider.Track className='bg-gradient-to-r from-success to-error' />
        <Slider.Thumb />
      </Slider.Root>
      <Slider.Input {...wiring.inputProps} />
    </Slider.Container>
  );
};

export const Range = (): ReactElement => {
  const [value, setValue] = useState<readonly number[]>([100]);

  const wiring = Slider.useWiring(
    value,
    useLazyRef(
      (): WiringOptions => ({
        onChange: setValue,
        range: { max: 5, min: 1 }
      })
    ).current
  );

  return (
    <Slider.Container label='Range'>
      <Slider.Archetype {...wiring.sliderProps} />
      <Slider.Input {...wiring.inputProps} />
    </Slider.Container>
  );
};

/** `<Sliders>` exhibit. */
export const Sliders = (): ReactElement => (
  <Exhibit title='Sliders'>
    <Column>
      <Gain />
      <Loudness />
      <Presence />
      <Range />

      <Slider.Container label='Compression'>
        <Slider.Archetype disabled value={[75]} />
        <Slider.Input disabled value='8' />
      </Slider.Container>
    </Column>
  </Exhibit>
);
